/**
 * The components shared by the two quota surfaces (A7Y-3802): the account table
 * on `/admin/automation` and the per-account detail page at
 * `/admin/automation/quota/:account`. Their shared VALUES live beside this file
 * in `quota-account-meta.ts`.
 *
 * They exist so the overview row and the detail page cannot drift into
 * rendering the same account differently — the split the ticket asked for is
 * how much of an account is shown, never what any of it means.
 */
import { Asterisk, Terminal } from 'lucide-react';
import type {
  AiQuotaAccountRole,
  AiQuotaProvider,
  AiQuotaScopedLimit,
  AiQuotaThresholds,
  AiQuotaWindow,
} from '@a7y/types';
import {
  formatQuotaPercent,
  formatQuotaResetInterval,
  formatQuotaResetRemaining,
  quotaBarClass,
} from './quota-display';
import { PROVIDER_META, ROLE_META } from './quota-account-meta';
import { fmtLocal } from './shared';

/** Renders nothing when the API predates the role field — never a guessed role. */
export function RoleBadge({ role }: { role: AiQuotaAccountRole | undefined }) {
  if (!role) return null;
  const meta = ROLE_META[role];
  if (!meta) return null;
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${meta.badge}`}
      title={meta.title}
      data-testid={`quota-account-role-${role}`}
    >
      {meta.label}
    </span>
  );
}

/**
 * The service an account signs in to, as a glyph rather than the words
 * `CLAUDE` / `CODEX` (TPF, A7Y-3802). Because it carries meaning rather than
 * decoration it also carries an accessible name and a title — it is never a
 * bare shape.
 */
export function ProviderIcon({ provider }: { provider: AiQuotaProvider }) {
  const meta = PROVIDER_META[provider];
  // An unrecognised provider is named rather than drawn — a glyph we have no
  // meaning for would be a guess, and the string is at least the truth.
  if (!meta) {
    return <span className="text-[11px] uppercase text-zinc-500">{provider}</span>;
  }
  const Icon = provider === 'codex' ? Terminal : Asterisk;
  return (
    <span
      className={`inline-flex items-center ${meta.className}`}
      title={meta.label}
      aria-label={meta.label}
      role="img"
      data-testid={`quota-provider-icon-${provider}`}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
    </span>
  );
}

/**
 * The per-model weekly limits, under the account-wide weekly bar they sit
 * beside (A7Y-3814).
 *
 * WHAT THESE SAY, and the whole reason they render separately from the weekly
 * percentage rather than folded into it (TPF, 2026-09-09): a model at 100%
 * cannot be used on this account until the weekly window cycles. The account is
 * not out of quota — everything routed to another model still runs — so this
 * never colours or replaces the weekly figure above it. Measured that day on
 * `tpf`: weekly 86%, Fable 100%. Both true, about different windows, and before
 * this only the first was visible anywhere.
 *
 * `null` is not `[]`. A failed probe, a codex account and a row older than the
 * field all mean "not read"; a Claude account that reported no per-model limit
 * renders nothing, because there is nothing to say.
 */
export function ScopedLimits({
  limits,
  now,
}: {
  limits: AiQuotaScopedLimit[] | null | undefined;
  now: number;
}) {
  if (limits == null || limits.length === 0) return null;
  // Worst first: the reader is looking for the model they cannot use.
  const ordered = [...limits].sort((a, b) => b.usedPercent - a.usedPercent);
  return (
    <div className="space-y-0.5">
      {ordered.map((limit) => {
        const spent = limit.usedPercent >= 100;
        const resets = limit.resetsAt ? formatQuotaResetInterval(limit.resetsAt, now) : null;
        return (
          <div
            key={limit.model}
            className="flex items-baseline gap-1 text-[11px] whitespace-nowrap tabular-nums"
            title={
              spent
                ? `${limit.model} cannot be used on this account until the weekly window cycles${
                    limit.resetsAt ? ` — ${fmtLocal(limit.resetsAt)}` : ''
                  }`
                : `${limit.model} weekly limit${
                    limit.resetsAt ? ` — resets ${fmtLocal(limit.resetsAt)}` : ''
                  }`
            }
            data-testid={`quota-scoped-limit-${limit.model}`}
          >
            <span className="text-zinc-500 dark:text-zinc-400">{limit.model}</span>
            <span
              className={
                spent
                  ? 'font-semibold text-red-700 dark:text-red-400'
                  : 'text-zinc-600 dark:text-zinc-300'
              }
            >
              {formatQuotaPercent(limit.usedPercent)}
            </span>
            {spent ? (
              <span className="text-red-700 dark:text-red-400">
                — unavailable{resets ? ` for ${resets}` : ''}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * How much of the window's reset instant to show beneath the bar.
 *
 * `interval` is the account table (TPF, 2026-09-09): the countdown sits under
 * the bar it belongs to, which is what let the separate `Resets` column go —
 * that column made the reader carry "S" and "W" across three columns to pair a
 * countdown with its own percentage. `full` is the detail page, which has the
 * room for the absolute time as well. `none` is anywhere the reset is not the
 * question.
 */
export type QuotaResetDisplay = 'none' | 'interval' | 'full';

/**
 * The bare countdown under a bar.
 *
 * A window at 0% carries no reset instant because the provider names one only
 * while a window is OPEN — measured 2026-09-09, all five Claude accounts idle
 * at 0% session reported `five_hour.resets_at: null` while the one with usage
 * carried an instant. So "nothing to reset" and "the provider owed us a time
 * and did not send it" read differently here rather than sharing a dash.
 */
function ResetInterval({ value, now }: { value: AiQuotaWindow; now: number }) {
  const interval = value.resetsAt ? formatQuotaResetInterval(value.resetsAt, now) : null;
  if (interval) {
    return (
      <div
        className="text-[11px] tabular-nums text-zinc-500 dark:text-zinc-400"
        title={`Resets ${fmtLocal(value.resetsAt!)}`}
      >
        resets in {interval}
      </div>
    );
  }
  return (
    <div
      className="text-[11px] text-zinc-400"
      title={
        value.usedPercent === 0
          ? 'No window running, so there is nothing to reset'
          : 'The provider reported no reset time'
      }
    >
      {value.usedPercent === 0 ? 'no window running' : 'reset time not reported'}
    </div>
  );
}

/**
 * A quota window as a percentage, a threshold-coloured bar, and optionally when
 * it resets. `reset` is the only difference between the compact table cell and
 * the full-width detail block — the number, the colour band and the "not
 * reported" wording are identical on both, by construction.
 */
export function QuotaBar({
  label,
  value,
  thresholds,
  now,
  reset,
}: {
  label: string;
  value: AiQuotaWindow | null;
  // The account's own admission boundaries, as the API read them out of the
  // single policy. This component states no boundary of its own (A7Y-2821).
  thresholds: AiQuotaThresholds | undefined;
  now: number;
  reset: QuotaResetDisplay;
}) {
  if (!value) {
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-3 text-xs">
          {label ? (
            <span className="font-medium text-zinc-700 dark:text-zinc-300">{label}</span>
          ) : null}
          <span className="text-zinc-400">Not reported</span>
        </div>
        <div className="h-1.5 rounded-full bg-zinc-100 dark:bg-zinc-800" />
      </div>
    );
  }

  const width = Math.max(0, Math.min(100, value.usedPercent));
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-3 text-xs">
        {label ? (
          <span className="font-medium text-zinc-700 dark:text-zinc-300">{label}</span>
        ) : null}
        <span className="font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">
          {formatQuotaPercent(value.usedPercent)} used
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
        <div
          className={`h-full rounded-full transition-[width] ${quotaBarClass(value.usedPercent, thresholds)}`}
          style={{ width: `${width}%` }}
        />
      </div>
      {reset === 'full' ? (
        <div className="text-[11px] text-zinc-400">
          {value.resetsAt
            ? `Resets ${fmtLocal(value.resetsAt)} · ${formatQuotaResetRemaining(value.resetsAt, now)}`
            : 'Reset time not reported'}
        </div>
      ) : null}
      {reset === 'interval' ? <ResetInterval value={value} now={now} /> : null}
    </div>
  );
}
